/**
 * Admin Applications dashboard — list, search, view, download ZIP, view docs.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    GraduationCap, FileSearch, Download, FileText, Mail, Loader2,
} from "lucide-react";

const STATUS_LABELS = {
    draft: ["bg-amber-100 text-amber-800 border-amber-300", "Draft"],
    submitted: ["bg-emerald-100 text-emerald-800 border-emerald-300", "Submitted"],
};

function StatusBadge({ status }) {
    const [cls, label] = STATUS_LABELS[status] || ["bg-muted text-muted-foreground", status || "—"];
    return <Badge variant="outline" className={`${cls} text-[10px]`}>{label}</Badge>;
}

function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" }); }
    catch { return iso; }
}

function fmtError(e, fb) { return e?.response?.data?.detail || e?.message || fb; }

async function downloadZipForApp(app) {
    try {
        const res = await api.get(
            `/admin/applications/${app.id}/zip`,
            { responseType: "blob" },
        );
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `application_${app.student_id || app.id}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    } catch (e) {
        toast.error(fmtError(e, "Could not download ZIP"));
    }
}

export default function AdminApplications() {
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [reviewing, setReviewing] = useState(null);

    const load = useCallback(async () => {
        try {
            const params = {};
            if (q) params.q = q;
            if (statusFilter) params.status = statusFilter;
            const { data } = await api.get("/admin/applications", { params });
            setItems(data.items || []);
            setCounts(data.counts || {});
        } catch (e) {
            toast.error(fmtError(e, "Could not load applications"));
        } finally {
            setLoading(false);
        }
    }, [q, statusFilter]);

    useEffect(() => { load(); }, [load]);

    const downloadZip = downloadZipForApp;

    return (
        <div className="space-y-6 max-w-7xl mx-auto px-6 py-10 pb-24 w-full min-w-0" data-testid="admin-applications">
            <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#C79520] mb-1 flex items-center gap-1.5">
                    <GraduationCap className="h-3 w-3" />
                    Heritage Community College
                </div>
                <h1 className="font-display text-3xl text-[#134B30]">Applications</h1>
                <p className="text-sm text-muted-foreground">
                    Incoming student admission applications.
                </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Card className="p-3 bg-[#FBF5E8] border-[#E1DAC5]">
                    <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">Total</div>
                    <div className="font-display text-2xl mt-1 text-[#134B30]">{counts.total ?? items.length}</div>
                </Card>
                <Card className="p-3 bg-amber-50 border-amber-200">
                    <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">Drafts</div>
                    <div className="font-display text-2xl mt-1 text-[#134B30]">{counts.draft || 0}</div>
                </Card>
                <Card className="p-3 bg-emerald-50 border-emerald-200">
                    <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">Submitted</div>
                    <div className="font-display text-2xl mt-1 text-[#134B30]">{counts.submitted || 0}</div>
                </Card>
            </div>

            <div className="flex gap-2 flex-wrap">
                <Input
                    placeholder="Search by student ID, name, email, program, or campus…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="max-w-xl"
                    data-testid="apps-search"
                />
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="h-9 px-2 text-sm border border-input rounded-md bg-background"
                    data-testid="apps-status-filter"
                >
                    <option value="">All statuses</option>
                    <option value="draft">Draft</option>
                    <option value="submitted">Submitted</option>
                </select>
            </div>

            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-12 flex items-center justify-center text-muted-foreground">
                            <Loader2 className="h-6 w-6 animate-spin" />
                        </div>
                    ) : items.length === 0 ? (
                        <div className="p-12 text-center text-sm text-muted-foreground">
                            <FileSearch className="h-8 w-8 mx-auto text-[#C79520]/60 mb-2" />
                            No applications match your search.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="apps-table">
                                <thead className="bg-muted/40 text-xs uppercase tracking-[0.14em] text-muted-foreground">
                                    <tr>
                                        <th className="text-left px-4 py-3 font-semibold">Student ID</th>
                                        <th className="text-left px-4 py-3 font-semibold">Applicant</th>
                                        <th className="text-left px-4 py-3 font-semibold">Program / Term</th>
                                        <th className="text-left px-4 py-3 font-semibold">Campus</th>
                                        <th className="text-left px-4 py-3 font-semibold">Docs</th>
                                        <th className="text-left px-4 py-3 font-semibold">Status</th>
                                        <th className="text-left px-4 py-3 font-semibold">Submitted</th>
                                        <th className="text-right px-4 py-3 font-semibold">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((a) => (
                                        <tr key={a.id} className="border-t border-border" data-testid={`apps-row-${a.id}`}>
                                            <td className="px-4 py-3 font-mono text-xs">{a.student_id}</td>
                                            <td className="px-4 py-3">
                                                <div className="font-medium">{a.first_name} {a.last_name}</div>
                                                <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
                                                    <Mail className="h-3 w-3" />{a.email}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-xs">
                                                <div className="capitalize">{a.program_category}</div>
                                                <div className="text-muted-foreground">{a.admission_term}</div>
                                            </td>
                                            <td className="px-4 py-3 text-xs">{a.campus}</td>
                                            <td className="px-4 py-3 text-xs">{(a.documents || []).length}</td>
                                            <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                                            <td className="px-4 py-3 text-xs">{fmtDate(a.submitted_at || a.created_at)}</td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="inline-flex gap-1">
                                                    <Button size="sm" variant="outline" onClick={() => setReviewing(a)} data-testid={`apps-view-${a.id}`}>
                                                        <FileSearch className="h-3 w-3 mr-1" /> View
                                                    </Button>
                                                    {a.status === "submitted" && (
                                                        <Button size="sm" variant="outline" onClick={() => downloadZip(a)} data-testid={`apps-zip-${a.id}`} className="border-[#C79520]/50 text-[#C79520]">
                                                            <Download className="h-3 w-3 mr-1" /> ZIP
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <Dialog open={!!reviewing} onOpenChange={(o) => !o && setReviewing(null)}>
                <DialogContent className="max-w-3xl" data-testid="apps-detail-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">
                            Application — {reviewing?.first_name} {reviewing?.last_name}
                        </DialogTitle>
                    </DialogHeader>
                    {reviewing && (
                        <div className="space-y-4 text-sm">
                            <div className="bg-[#FBF5E8] border border-[#E1DAC5] rounded-md p-3 grid grid-cols-2 gap-2 text-xs">
                                <div><strong>Student ID:</strong> {reviewing.student_id}</div>
                                <div><strong>Status:</strong> <StatusBadge status={reviewing.status} /></div>
                                <div><strong>Email:</strong> {reviewing.email}</div>
                                <div><strong>Phone:</strong> {reviewing.phone || "—"}</div>
                                <div><strong>DOB:</strong> {fmtDate(reviewing.date_of_birth)}</div>
                                <div><strong>Country:</strong> {reviewing.country}</div>
                                <div><strong>Program:</strong> {reviewing.program_id}</div>
                                <div><strong>Category:</strong> {reviewing.program_category}</div>
                                <div><strong>Term:</strong> {reviewing.admission_term}</div>
                                <div><strong>Campus:</strong> {reviewing.campus}</div>
                                {reviewing.country?.toLowerCase() === "canada" && (
                                    <div><strong>SIN:</strong> {reviewing.sin || "—"}</div>
                                )}
                                <div className="col-span-2"><strong>Address:</strong> {[reviewing.address_line1, reviewing.address_line2, reviewing.city, reviewing.state_province, reviewing.postal_code].filter(Boolean).join(", ") || "—"}</div>
                            </div>
                            <div>
                                <div className="text-sm font-semibold mb-2">Uploaded documents</div>
                                {(reviewing.documents || []).length === 0 ? (
                                    <div className="text-xs text-muted-foreground italic">No documents uploaded.</div>
                                ) : (
                                    <ul className="space-y-1">
                                        {reviewing.documents.map((d) => (
                                            <li key={d.id} className="flex items-center justify-between text-xs bg-muted/20 rounded px-2 py-1.5">
                                                <span className="flex items-center gap-1.5 truncate">
                                                    <FileText className="h-3 w-3 text-[#134B30] flex-shrink-0" />
                                                    <Badge variant="outline" className="text-[10px]">{d.category_label}</Badge>
                                                    <span className="truncate">{d.filename}</span>
                                                    <span className="text-muted-foreground">({Math.round(d.size / 1024)} KB)</span>
                                                </span>
                                                <a
                                                    href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/applications/${reviewing.id}/documents/${d.id}/download`}
                                                    download={d.filename}
                                                    className="text-[#134B30] hover:underline inline-flex items-center gap-1"
                                                    data-testid={`apps-download-doc-${d.id}`}
                                                >
                                                    <Download className="h-3 w-3" />
                                                    Download
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setReviewing(null)}>Close</Button>
                        {reviewing?.status === "submitted" && (
                            <Button onClick={() => downloadZip(reviewing)} className="bg-[#C79520] hover:bg-[#B17E0F] text-[#0A2818] gap-1.5">
                                <Download className="h-4 w-4" />
                                Download ZIP
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
